import {
  Col,
  Container,
  ListGroup,
  ListGroupItem,
  ListGroupItemHeading,
  Row,
  Button,
  Alert,
  Card,
  CardBody,
} from "reactstrap";
import BreadCrumb from "../../components/Common/BreadCrumb";
import { Link } from "react-router-dom";
import useAuthUser from "../../components/Hooks/useAuthUser";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { translateApiError } from "../../helpers/translateApiError";
import {
  checkTenantHubspot,
  getHubspotOauthUrl,
  disconnectHubspot,
} from "./api/hubspotApi";

const HubSpotIntegration = ({
  cardTitle = "HubSpot Integration",
  ...props
}) => {
  const { t } = useTranslation();
  document.title = t("common.pageTitle", {
    page: t("hubspotIntegration.pageTitle"),
  });
  const { tenantProfile } = useAuthUser();
  const [isLoading, setIsLoading] = useState(false);
  const [connectionError, setConnectionError] = useState(null);

  // Query HubSpot connection status
  const {
    data: hubspotStatus,
    isLoading: isCheckingConnection,
    error: queryError,
    refetch: refetchConnection,
  } = useQuery({
    queryKey: ["hubspot-status", tenantProfile?.handle],
    queryFn: () => checkTenantHubspot(tenantProfile.handle),
    enabled: !!tenantProfile?.handle,
    retry: 2,
    staleTime: 5000, // Consider data fresh for 30 seconds
  });

  const hubspotFeatures = [
    {
      title: t("hubspotIntegration.features.automate.title"),
      description: t("hubspotIntegration.features.automate.description"),
    },
    {
      title: t("hubspotIntegration.features.schedule.title"),
      description: t("hubspotIntegration.features.schedule.description"),
    },
    {
      title: t("hubspotIntegration.features.track.title"),
      description: t("hubspotIntegration.features.track.description"),
    },
  ];

  // Get connection status from API response
  const isConnected = hubspotStatus?.data?.connected || false;
  const connectionStatus = hubspotStatus?.data?.status;
  const hubspotHubDomain = hubspotStatus?.data?.hubspotHubDomain;
  const connectedAt = hubspotStatus?.data?.connectedAt;

  // Check HubSpot connection status
  const handleConnectToHubSpot = async () => {
    if (!tenantProfile.handle) {
      setConnectionError(t("integration.errors.tenantMissing"));
      return;
    }

    setIsLoading(true);
    setConnectionError(null);
    console.log("Opening HubSpot OAuth");
    try {
      // Get OAuth URL from the authenticated API
      const response = await getHubspotOauthUrl(tenantProfile.handle);
      const oauthUrl = response.oauthUrl;

      // Open OAuth flow in a new tab
      const newTab = window.open(oauthUrl, "_blank", "noopener,noreferrer");

      // If popup opened successfully, listen for OAuth completion
      if (newTab) {
        const checkClosed = setInterval(() => {
          try {
            // Check if the tab is closed
            if (newTab.closed) {
              clearInterval(checkClosed);
              setIsLoading(false);
              // Refetch connection status after OAuth flow
              refetchConnection();
            }
          } catch (error) {
            // If we can't access newTab.closed, assume it's still open
            // This can happen due to CORS restrictions
          }
        }, 1000);
      } else {
        // Silently fail - user will need to manually retry if popup was blocked
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Failed to connect to HubSpot:", error);
      setConnectionError(t("integration.hubspot.connectFailed"));
      setIsLoading(false);
    }
  };

  const handleDisconnectFromHubSpot = async () => {
    if (!tenantProfile.handle) {
      setConnectionError(t("integration.errors.tenantMissing"));
      return;
    }

    setIsLoading(true);
    setConnectionError(null);

    try {
      await disconnectHubspot(tenantProfile.handle);
      // Refetch connection status after disconnect
      await refetchConnection();
    } catch (error) {
      console.error("Failed to disconnect from HubSpot:", error);
      setConnectionError(t("integration.hubspot.disconnectFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleReconnectToHubSpot = async () => {
    // For reconnection, we can reuse the connect logic
    await handleConnectToHubSpot();
  };

  const getStatusMessage = () => {
    switch (connectionStatus) {
      case "connected":
        return t("hubspotIntegration.statusMessage.connected");
      case "refresh_failed":
        return t("hubspotIntegration.statusMessage.refreshFailed");
      case "invalid_token":
        return t("hubspotIntegration.statusMessage.invalidToken");
      case "not_connected":
      default:
        return t("hubspotIntegration.statusMessage.notConnected");
    }
  };

  const getStatusColor = () => {
    switch (connectionStatus) {
      case "connected":
        return "success";
      case "refresh_failed":
      case "invalid_token":
        return "warning";
      default:
        return "secondary";
    }
  };

  const needsReconnection =
    connectionStatus === "refresh_failed" ||
    connectionStatus === "invalid_token";

  const ConnectedView = () => (
    <Card className="shadow-sm">
      <CardBody>
        <div className="text-center mb-4">
          <div className="mb-3">
            <img
              src="/path-to-hubspot-logo.png"
              alt={t("hubspotIntegration.logoAlt")}
              style={{ height: "40px" }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </div>
          <Alert color={getStatusColor()} className="mb-3">
            <h4 className="alert-heading mb-0">{getStatusMessage()}</h4>
          </Alert>

          {hubspotHubDomain && (
            <p className="text-muted mb-2">
              <strong>{t("hubspotIntegration.hubDomain")}</strong>{" "}
              {hubspotHubDomain}
            </p>
          )}

          {connectedAt && (
            <p className="text-muted mb-4">
              <strong>{t("hubspotIntegration.connectedAt")}</strong>{" "}
              {new Date(connectedAt).toLocaleDateString()}
            </p>
          )}

          <p className="text-muted mb-4">{t("hubspotIntegration.learnMore")}</p>
          <div className="d-grid gap-2">
            <Button
              color={needsReconnection ? "warning" : "secondary"}
              size="lg"
              onClick={handleReconnectToHubSpot}
              disabled={isLoading}
            >
              {isLoading
                ? t("hubspotIntegration.reconnecting")
                : needsReconnection
                ? t("hubspotIntegration.reconnectRequired")
                : t("hubspotIntegration.reconnect")}
            </Button>
          </div>
        </div>
        <div className="text-center">
          <Link
            to="#"
            className="text-danger text-decoration-underline"
            onClick={(e) => {
              e.preventDefault();
              handleDisconnectFromHubSpot();
            }}
          >
            {isLoading
              ? t("hubspotIntegration.disconnecting")
              : t("hubspotIntegration.disconnect")}
          </Link>
        </div>
      </CardBody>
    </Card>
  );

  const NotConnectedView = () => (
    <Card className="shadow-sm">
      <CardBody>
        <div className="text-center mb-4">
          <div className="mb-3">
            <img
              src="/path-to-hubspot-logo.png"
              alt={t("hubspotIntegration.logoAlt")}
              style={{ height: "40px" }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </div>
          <h4 className="mb-3">{t("hubspotIntegration.notConnectedTitle")}</h4>
          <p className="text-muted mb-4">
            {t("hubspotIntegration.notConnectedPrefix")}{" "}
            <Link to="#" className="text-primary">
              {t("hubspotIntegration.notConnectedLink")}
            </Link>
            {t("hubspotIntegration.notConnectedSuffix")}
          </p>

          {(connectionError || queryError) && (
            <Alert color="danger" className="mb-3">
              {connectionError ||
                translateApiError(
                  queryError,
                  t("hubspotIntegration.checkStatusFailed")
                )}
            </Alert>
          )}

          <Button
            color="primary"
            size="lg"
            className="mb-4"
            onClick={handleConnectToHubSpot}
            disabled={isLoading || !tenantProfile.handle}
          >
            {isLoading
              ? t("hubspotIntegration.connecting")
              : t("hubspotIntegration.connect")}
          </Button>
        </div>

        <div className="mt-5">
          <div className="text-center mb-4">
            <div className="mb-3">
              <span className="text-primary" style={{ fontSize: "2rem" }}>
                🔗
              </span>
            </div>
            <h5 className="mb-4">{t("hubspotIntegration.benefitsTitle")}</h5>
          </div>

          <ListGroup className="detail-list" tag="dl">
            {hubspotFeatures.map((feature, index) => (
              <ListGroupItem tag="div" key={index} className="border-0 px-0">
                <ListGroupItemHeading tag="dt" className="fw-bold">
                  {feature.title}
                </ListGroupItemHeading>
                {feature.description && (
                  <dd className="text-muted mb-3">{feature.description}</dd>
                )}
              </ListGroupItem>
            ))}
          </ListGroup>

          <div className="text-center mt-4">
            <Button
              color="primary"
              size="lg"
              onClick={handleConnectToHubSpot}
              disabled={isLoading || !tenantProfile.handle}
            >
              {isLoading
                ? t("hubspotIntegration.connecting")
                : t("hubspotIntegration.connect")}
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );

  // Show loading state while checking connection
  if (isCheckingConnection) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("hubspotIntegration.pageTitle")}
            pageTitle={t("hubspotIntegration.breadcrumbParent")}
          />
          <Row>
            <Col xxl="3"></Col>
            <Col xxl="6">
              <Card className="shadow-sm">
                <CardBody>
                  <div className="text-center">
                    <div
                      className="spinner-border text-primary mb-3"
                      role="status"
                    >
                      <span className="visually-hidden">
                        {t("common.loading")}
                      </span>
                    </div>
                    <p>{t("hubspotIntegration.checkingStatus")}</p>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xxl="3"></Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("hubspotIntegration.pageTitle")}
          pageTitle={t("hubspotIntegration.breadcrumbParent")}
        />
        <Row>
          <Col xxl="3"></Col>
          <Col xxl="6">
            {isConnected ? <ConnectedView /> : <NotConnectedView />}
          </Col>
          <Col xxl="3"></Col>
        </Row>
      </Container>
    </div>
  );
};

export default HubSpotIntegration;
